// Why an annotation row could not be read on this device, and what each state
// says in place of the item. The reasons are set by the read path
// (src/utils/courses-annotations-read-path.js) and read by the Notes Hub and the
// lesson reader. Copy comes from the encryption spec,
// docs/plans/2026-09-02-next-steps-e2e-encryption.md: section 6.3 for the
// per-item sentence ("An unknown v is shown as 'This note needs a newer app
// version', never as garbage") and 6.7 for the old-client line and the
// "Approve this device" screen the placeholder stands in for.
export const UNREADABLE_REASON = Object.freeze({
  // The envelope is a version this build cannot decode — or this build carries
  // no decoder at all, which is the same thing to the member: update the app.
  NEEDS_NEWER_APP: 'needs-newer-app',
  // The envelope could be decoded, but this device holds no master key yet. The
  // member is enrolled elsewhere; this device needs approving.
  NO_KEY: 'no-key',
  // The envelope did not open: tampered, a key generation this device does not
  // hold, or corrupt. Shown as an error for that item, never dropped.
  FAILED: 'failed',
  /**
   * The row is on the generation a RUNNING rotation is moving to, and this
   * device is not the one running it (#1694, spec 6.12).
   *
   * ⚠️ IT IS NOT `NO_KEY`, WHICH IS WHAT IT USED TO SAY. That reason renders as
   * "Approve this device to read this note" — an instruction the member cannot
   * act on, because the device is already approved. And it appears on row after
   * row as the walk passes over them, so the member's other phone shows their
   * notes turning into errors one at a time while nothing whatever is wrong.
   *
   * Temporary by nature: the moment the rotation commits, this device is
   * revoked and re-approved with MK2, and every one of these rows opens.
   */
  BEING_REENCRYPTED: 'being-re-encrypted',
  /**
   * The row was damaged before a sign-out — it did not open on the device
   * holding the very key it names — and the sign-out carried it forward under
   * the new key as a labelled shell rather than stall on it for ever
   * (mobile#1768). Permanent: nothing anywhere can open what it used to say.
   * Kept, not dropped, so the member sees that something was here and can
   * delete it themselves.
   */
  UNRECOVERABLE: 'unrecoverable',
});

/**
 * The one plaintext field a sealed row may carry that is not a column of its
 * type: `true` on a shell the sign-out wrote in place of a row it could not
 * open. Sealed like every other field, so it says what it says only to a
 * device holding the key; the read path turns it into UNRECOVERABLE and never
 * hands it on as a field.
 */
export const UNRECOVERABLE_FIELD = 'unrecoverable';

// Per item, typed, from the note sentence the spec gives.
export const NEEDS_NEWER_APP_COPY = Object.freeze({
  note: 'This note needs a newer app version',
  highlight: 'This highlight needs a newer app version',
  tag: 'This tag needs a newer app version',
  blank: 'This answer needs a newer app version',
});

export const FAILED_COPY = Object.freeze({
  note: 'This note could not be opened on this device',
  highlight: 'This highlight could not be opened on this device',
  tag: 'This tag could not be opened on this device',
  blank: 'This answer could not be opened on this device',
});

export const UNRECOVERABLE_COPY = Object.freeze({
  note: 'This note was damaged and could not be recovered',
  highlight: 'This highlight was damaged and could not be recovered',
  tag: 'This tag was damaged and could not be recovered',
  blank: 'This answer was damaged and could not be recovered',
});
export const NO_KEY_COPY = Object.freeze({
  note: 'Approve this device to read this note',
  highlight: 'Approve this device to read this highlight',
  tag: 'Approve this device to read this tag',
  blank: 'Approve this device to read this answer',
});

// Named where it can be, because "on your other phone" is the difference
// between a fault and an explanation — and unnamed rather than guessed when the
// status does not carry a label for the device.
export const BEING_REENCRYPTED_COPY = Object.freeze({
  note: 'This note is being re-encrypted',
  highlight: 'This highlight is being re-encrypted',
  tag: 'This tag is being re-encrypted',
  blank: 'This answer is being re-encrypted',
});

/** With the device that is doing it, when the status names one. */
export function beingReencryptedCopy(type, deviceName) {
  const base = BEING_REENCRYPTED_COPY[type] || BEING_REENCRYPTED_COPY.note;
  return deviceName ? `${base} on ${deviceName}` : base;
}

const COPY_BY_REASON = Object.freeze({
  [UNREADABLE_REASON.NEEDS_NEWER_APP]: NEEDS_NEWER_APP_COPY,
  [UNREADABLE_REASON.NO_KEY]: NO_KEY_COPY,
  [UNREADABLE_REASON.FAILED]: FAILED_COPY,
  [UNREADABLE_REASON.BEING_REENCRYPTED]: BEING_REENCRYPTED_COPY,
  [UNREADABLE_REASON.UNRECOVERABLE]: UNRECOVERABLE_COPY,
});

// The sentence that stands in for one item, by type and reason.
//
// `deviceName` is the row's `reencryptingOn`, and only BEING_REENCRYPTED uses
// it — every other reason is about THIS device, so naming another one would be
// noise.
//
// OPTIONAL, and blank answers are why. Notes, highlights and tags reach their
// screens as rows, so the name travels with them. A blank reaches its block as
// a bare reason out of `unreadableBlanks` (a { blockId: reason } map), and
// widening that map to carry a device name would touch the store, the renderer
// and both blank components to add three words. The sentence — which is the
// part that stops the member thinking their answer is broken — is the same
// either way.
export function unreadableCopy(type, reason, deviceName = null) {
  if (reason === UNREADABLE_REASON.BEING_REENCRYPTED) return beingReencryptedCopy(type, deviceName);
  return COPY_BY_REASON[reason]?.[type] || FAILED_COPY[type] || FAILED_COPY.note;
}

// A tag pill has no room for a sentence; this is the label, and the sentence
// goes in its title.
export const UNREADABLE_SHORT_COPY = Object.freeze({
  [UNREADABLE_REASON.NEEDS_NEWER_APP]: 'Needs newer app',
  [UNREADABLE_REASON.NO_KEY]: 'Approve device',
  [UNREADABLE_REASON.FAILED]: 'Could not open',
  [UNREADABLE_REASON.BEING_REENCRYPTED]: 'Re-encrypting',
  [UNREADABLE_REASON.UNRECOVERABLE]: 'Unrecoverable',
});

export function unreadableShortCopy(reason) {
  return UNREADABLE_SHORT_COPY[reason] || UNREADABLE_SHORT_COPY[UNREADABLE_REASON.FAILED];
}

// What stands where the member's marks would be, for the whole view. Locked
// wins: it says nothing on this device opens, where needing a newer app leaves
// the readable marks readable. The store's `annotationsAccessState` getter
// applies that order once for every screen.
export const ACCESS_NOTICE_STATE = Object.freeze({
  LOCKED: 'locked',
  NEEDS_NEWER_APP: 'needs-newer-app',
  FAILED: 'failed',
});

// The whole-view line when some rows did not open and nothing else explains it.
// Not in the spec, which names no copy for a row that fails to authenticate; it
// exists so that outcome is never silent in the reader, where a highlight with no
// position has nowhere else to be shown.
export const SOME_FAILED_COPY =
  'Some of your notes and highlights could not be opened on this device.';

// The whole-view line for a build that cannot read what the member wrote
// elsewhere (spec 6.7, "Old clients").
export const UPDATE_APP_COPY = 'Update the app to see your notes';

// The Notes Hub header line while the plaintext migration is running (#1545,
// spec 6.7: "Batches of 20 with a progress line in the Hub header ('Securing
// your notes, 14 of 62')"). Here rather than in the migration module for the
// same reason the sentences above are: this file is where a reviewer looks for
// what the feature says to a member.
export function migrationProgressCopy({ done, total }) {
  return `Securing your notes, ${done} of ${total}`;
}

// What stands in for the member's marks on a device with no key, and the label
// on the button that leaves for the real screen (spec 6.7, screen 2). The
// notice itself is the brief moment before the Hub redirects, and the whole of
// what the reader shows — the reader does not redirect, because a member who
// opened a lesson asked for the lesson.
export const APPROVE_DEVICE_COPY = Object.freeze({
  title: 'Approve this device',
  body: 'Open HOGC One on a device you already use and tap Approve to read your notes here.',
  action: 'Approve this device',
});

// The screens the approval flow can leave for, by path.
//
// `approveDevice` is registered by the device-approval ticket (#1546) and
// `startOver` by the reset one (#1549). `devices` and `recover` are #1547's
// (recovery code, Devices) and are still unregistered. All four are named here
// so a screen can link to another ticket's path without guessing it — the PRs
// meet in this one object rather than in hard-coded strings that have to agree.
//
// Whether a path is registered YET is answered by `isRegistered` at the point
// of use, never by this comment: every link to one of these is gated on it, so
// a screen cannot offer a route that does not exist.
//
// Every path sits under the static `/courses/devices` prefix, which the router
// must declare BEFORE `/courses/:programSlug` so a program slug cannot shadow
// it (router/index.js says why the static-before-param order is load-bearing).
export const ENCRYPTION_ROUTES = Object.freeze({
  /** Screen 2: this device has no key and is waiting to be approved. */
  approveDevice: '/courses/devices/approve',
  /** #1547: the member's devices, with revoke. */
  devices: '/courses/devices',
  /** #1547: type the recovery code to unwrap MK here. */
  recover: '/courses/devices/recover',
  /** Screens 6 and 7: erase the bundle and start again, or the hold that is running. */
  startOver: '/courses/devices/start-over',
});

/**
 * The approval screen, carrying where the member was so it can put them back
 * there once the key arrives.
 *
 * Here rather than in either page because both the Hub and the reader build it,
 * and a `?from=` the screen cannot read is a member returned to the wrong place
 * — or, unencoded, a query string that swallows the rest of the path.
 *
 * @param {string} [from] the full path to return to
 * @returns {string}
 */
export function approveDevicePath(from) {
  if (!from) return ENCRYPTION_ROUTES.approveDevice;
  return `${ENCRYPTION_ROUTES.approveDevice}?from=${encodeURIComponent(from)}`;
}

// Deliberately outside `ENCRYPTION_ROUTES`: that map is where #1546, #1547 and
// #1549 meet, and this screen is linked to only from within #1547 — the Devices
// screen and the Hub's recovery nudge are its two entry points — so it would be
// one more path for the other tickets to code against for nothing.
export const RECOVERY_CODE_ROUTE = '/courses/devices/recovery-code';

/**
 * The passphrase screen (#1695), outside `ENCRYPTION_ROUTES` for the same
 * reason as the code above: it is linked to only from within this feature —
 * the Devices screen, and the approval screen's "use passphrase instead".
 */
export const PASSPHRASE_ROUTE = '/courses/devices/passphrase';

/**
 * "Sign out all other devices" (#1694, decision 50). One control, not a Revoke
 * per row: rotation is the only thing that makes a device stop being able to
 * read, so the two are the same operation and a per-row button would be a
 * promise the design cannot keep.
 */
export const SIGN_OUT_OTHERS_ROUTE = '/courses/devices/sign-out-others';

/**
 * The screens a new-device request must not cover (#1691).
 *
 * The request is asked on any route now — spec 6.7 screen 3 says "on next
 * launch", and the member is standing there with the new phone while this one
 * is the only thing that can answer, so waiting for them to open Next Steps is
 * waiting for the wrong thing. These are the exceptions, all for the same
 * reason: a modal would cover the control the member came for.
 *
 * `recovery-code` IS THE ONE THAT MATTERS. It shows the code once, holds it in
 * component data for the visit and loses it when the view is popped — the
 * member's own copy is the only one that outlives the screen — and it is
 * reached from the Devices screen and the Hub's nudge, both of which mean a
 * device that HOLDS the key, which is exactly the device this modal
 * interrupts. Covering a one-time secret and its "I have saved it" control is
 * the worst version of the harm this list exists to prevent.
 *
 * `start-over` has a form. `approve` and `recover` are belt-and-braces: both
 * are keyless-device screens, so `hasMasterKey` already keeps the request off
 * them, and they are listed so the rule reads as one rule rather than two.
 *
 * The Devices screen is deliberately NOT here: it lists the request with its
 * own Approve, and a member who walked there to answer it is answered either
 * way — the modal is the same decision, on top.
 *
 * Compared against matched route RECORDS, so keep them exactly as
 * `src/router/index.js` declares them.
 */
export const REQUEST_DEFERRING_PATHS = Object.freeze([
  ENCRYPTION_ROUTES.approveDevice,
  ENCRYPTION_ROUTES.recover,
  ENCRYPTION_ROUTES.startOver,
  RECOVERY_CODE_ROUTE,
  // #1695. The member is typing a secret into a field, and in `set` mode into
  // two that have to match. A modal over that loses what they typed and the
  // reason they came, and — like the recovery code — the screen is reached
  // from a device that HOLDS the key, which is exactly the device the request
  // interrupts.
  PASSPHRASE_ROUTE,
  // #1694, and the strongest case in this list. The screen shows a NEW recovery
  // code once, and covering it would cover the one-time secret AND the "I have
  // saved it" that sends the rotation. It also runs the re-seal: a modal over a
  // walk that says "you can close the app" invites exactly the interruption the
  // member was told was safe, at the moment it is least worth testing.
  SIGN_OUT_OTHERS_ROUTE,
]);
